import { useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, Chip, Typography, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Menu, MenuItem, Checkbox, ListItemText,
  Button, Snackbar, Alert, Avatar, alpha,
  IconButton, Tooltip, TextField, InputAdornment, Divider
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BalanceIcon from "@mui/icons-material/Balance";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StorageIcon from "@mui/icons-material/Storage";
import LanguageIcon from "@mui/icons-material/Language";

// ── Theme ─────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#0284c7" },
    secondary: { main: "#10b981" },
    background: { default: "#f5f7fa", paper: "#ffffff" },
    text: { primary: "#1e293b", secondary: "#475569" },
  },
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
    h5: { fontWeight: 800, letterSpacing: "-0.5px" },
    subtitle2: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.06)",
          border: "1px solid #e2e8f0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#f8fafc",
          color: "#475569",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          borderBottom: "1px solid #e2e8f0",
          padding: "10px 18px",
          whiteSpace: "nowrap",
        },
        body: {
          fontSize: 13,
          color: "#0f172a",
          borderBottom: "1px solid #f1f5f9",
          padding: "14px 18px",
          verticalAlign: "middle",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover td": { backgroundColor: "#f8faff" },
          "&:last-child td": { borderBottom: "none" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: 10, height: 20, letterSpacing: "0.03em" },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            backgroundColor: alpha("#0284c7", 0.04),
            "& fieldset": { borderColor: alpha("#0284c7", 0.2) },
            "&:hover fieldset": { borderColor: "#0284c7" },
            "&.Mui-focused fieldset": { borderColor: "#0284c7", borderWidth: 1.5 },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: 8, fontSize: 13 },
      },
    },
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function usageBadge(val) {
  if (val > 1) return { label: "High", color: "warning" };
  if (val > 0.1) return { label: "Normal", color: "success" };
  return { label: "Low", color: "default" };
}

const COL_DEFS = [
  { key: "name", label: "Service Name" },
  { key: "cluster", label: "Cluster" },
  { key: "type", label: "LB Type" },
  { key: "dns", label: "DNS Endpoint" },
  { key: "cost", label: "Cost Breakdown" },
  { key: "usage", label: "Data Usage" },
];

// ── Main Component ────────────────────────────────────────────────────────────
export default function EKSLoadBalancer({ loadBalancerData = null, clusterLabel = null, platform = "eks" }) {
  const RAW_DATA = loadBalancerData?.data ?? [];

  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState(RAW_DATA);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [colAnchor, setColAnchor] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const [visibleCols, setVisibleCols] = useState({
    name: true, cluster: true, type: true, dns: true, cost: true, usage: platform !== "aks",
  });

  useEffect(() => {
    setFiltered(RAW_DATA);
    setSearch("");
  }, [loadBalancerData]);

  useEffect(() => {
    const t = search.toLowerCase();
    if (!t) {
      setFiltered(RAW_DATA);
      return;
    }
    setFiltered(RAW_DATA.filter(lb =>
      (lb.serviceName || "").toLowerCase().includes(t) ||
      (lb.clusterName || "").toLowerCase().includes(t) ||
      (lb.namespace || "").toLowerCase().includes(t) ||
      (lb.loadBalancerDNSname || "").toLowerCase().includes(t) ||
      (lb.loadBalancerAddress || "").toLowerCase().includes(t)
    ));
  }, [search, RAW_DATA]);

  const sorted = [...filtered].sort((a, b) => {
    if (!sortCol) return 0;
    const vals = {
      name: [a.serviceName || "", b.serviceName || ""],
      cluster: [a.clusterName || "", b.clusterName || ""],
      type: [a.loadBalancerType || "", b.loadBalancerType || ""],
      dns: [a.loadBalancerDNSname || a.loadBalancerAddress || "",
      b.loadBalancerDNSname || b.loadBalancerAddress || ""],
      cost: [(a.loadBalancerCost || 0) + (a.loadBalancerDataProcessedCost || 0),
      (b.loadBalancerCost || 0) + (b.loadBalancerDataProcessedCost || 0)],
      usage: [a.loadBalancerDataProcessedUsage || 0, b.loadBalancerDataProcessedUsage || 0],
    };
    const [av, bv] = vals[sortCol];
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  function toggleCol(key) {
    setVisibleCols(p => ({ ...p, [key]: !p[key] }));
  }

  const copyDNS = useCallback((dns) => {
    if (!dns) return;
    navigator.clipboard.writeText(dns);
    setSnack({ open: true, msg: "DNS name copied to clipboard" });
  }, []);

  function exportCSV() {
    const header = "Service,Cluster,Namespace,Type,DNS,Total Cost,Data Usage (MB),Hours";
    const rows = filtered.map(lb => [
      lb.serviceName || "",
      lb.clusterName || "",
      lb.namespace || "",
      lb.loadBalancerType || "",
      lb.loadBalancerDNSname || lb.loadBalancerAddress || "",
      ((lb.loadBalancerCost || 0) + (lb.loadBalancerDataProcessedCost || 0)).toFixed(4),
      (lb.loadBalancerDataProcessedUsage || 0).toFixed(4),
      lb.loadBalancerRunningHours || ""
    ].join(","));

    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob),
      download: "load_balancers.csv"
    });
    a.click();
  }

  // Summary stats
  const totalCost = filtered.reduce((s, d) => s + (d.loadBalancerCost || 0) + (d.loadBalancerDataProcessedCost || 0), 0);
  const totalHours = filtered.reduce((s, d) => s + (d.loadBalancerRunningHours || 0), 0);
  const totalUsage = filtered.reduce((s, d) => s + (d.loadBalancerDataProcessedUsage || 0), 0);

  const isAzure = platform === "aks";

  const statCards = [
    { label: "Total Load Balancers", value: filtered.length, icon: <LanguageIcon />, color: "#0284c7", bg: "#e0f2fe" },
    { label: "Total Cost", value: `$${totalCost.toFixed(2)}`, icon: <AttachMoneyIcon />, color: "#10b981", bg: "#d1fae5" },
    { label: "Running Hours", value: totalHours.toFixed(1), icon: <AccessTimeIcon />, color: "#d97706", bg: "#fef3c7" },
    ...(!isAzure ? [{ label: "Data Processed", value: `${totalUsage.toFixed(2)} MB`, icon: <StorageIcon />, color: "#0369a1", bg: "#bae6fd" }] : []),
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: "background.default", p: { xs: 2, md: 3 } }}>

        {/* ── Header Card ───────────────────────────────────────────────────── */}
        <Card sx={{ mb: 2.5, overflow: "visible" }}>
          <CardContent sx={{ p: "20px 24px !important" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha("#0284c7", 0.1), color: "primary.main", width: 46, height: 46, borderRadius: "12px" }}>
                  <BalanceIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ lineHeight: 1.2, color: "text.primary" }}>
                    Load Balancers{clusterLabel ? ` — ${clusterLabel}` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                      Usage & cost analysis
                    </Typography>
                    <Chip
                      label={`${RAW_DATA.length} load balancer${RAW_DATA.length !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: alpha("#0284c7", 0.1), color: "primary.main" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ── Summary Stats ─────────────────────────────────────────────────── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: `repeat(${statCards.length}, 1fr)` }, gap: 2, mb: 3 }}>
          {statCards.map(card => (
            <Card key={card.label}>
              <CardContent sx={{ p: "18px 20px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: 11.5 }}>{card.label}</Typography>
                    <Typography variant="h5" sx={{ mt: 0.5, fontSize: 26, color: "text.primary" }}>{card.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: card.bg, color: card.color, width: 42, height: 42, borderRadius: "10px", "& svg": { fontSize: 20 } }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* ── Controls Bar ──────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            mb: 2.5,
            mt: 5,
            gap: 1.5,
            flexWrap: "wrap"
          }}
        >
          <TextField
            size="small"
            placeholder="Search services, clusters, DNS…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: { xs: "100%", sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { fontSize: 13 },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
            onClick={e => setColAnchor(e.currentTarget)}
            sx={{ height: 38, px: 2 }}
          >
            Columns
          </Button>

          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={exportCSV}
            sx={{ height: 38, px: 2 }}
          >
            Export
          </Button>
        </Box>

        {/* ── Column Toggle Menu ────────────────────────────────────────────── */}
        <Menu
          anchorEl={colAnchor}
          open={Boolean(colAnchor)}
          onClose={() => setColAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 200, mt: 0.5, border: "1px solid #e2e8f0" } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Toggle Columns
            </Typography>
          </Box>
          <Divider />
          {COL_DEFS.map(col => (
            <MenuItem key={col.key} dense onClick={() => toggleCol(col.key)} sx={{ gap: 0.5 }}>
              <Checkbox size="small" checked={visibleCols[col.key]} sx={{ p: 0.5, mr: 0.5, color: "primary.main" }} />
              <ListItemText primary={col.label} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          ))}
        </Menu>

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {COL_DEFS.filter(c => visibleCols[c.key]).map(col => (
                    <TableCell key={col.key}>
                      <TableSortLabel
                        active={sortCol === col.key}
                        direction={sortCol === col.key ? sortDir : "asc"}
                        onClick={() => handleSort(col.key)}
                        sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { opacity: sortCol === col.key ? 1 : 0.3 } }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No load balancers found.
                    </TableCell>
                  </TableRow>
                ) : sorted.map(lb => {
                  const totalCostRow = (lb.loadBalancerCost || 0) + (lb.loadBalancerDataProcessedCost || 0);
                  const usageVal = lb.loadBalancerDataProcessedUsage || 0;
                  const created = lb.serviceCreationTime || lb.loadBalancerCreatedTime
                    ? new Date(lb.serviceCreationTime || lb.loadBalancerCreatedTime).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : "—";
                  const { label: usageLabel, color: usageColor } = usageBadge(usageVal);

                  return (
                    <TableRow key={lb.id || lb.loadBalancerName}>
                      {visibleCols.name && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: alpha("#0284c7", 0.08), color: "primary.main", width: 36, height: 36, borderRadius: "9px", fontSize: 15, flexShrink: 0, mt: 0.2 }}>⚙</Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontSize: 13 }}>{lb.serviceName || lb.loadBalancerName}</Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.4 }}
                              >
                                {lb.namespace}
                                <br />
                                Created: {created}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.cluster && (
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{lb.clusterName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(lb.attachedInstances?.length || 0)} instance{(lb.attachedInstances?.length || 0) !== 1 ? "s" : ""}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleCols.type && (
                        <TableCell>
                          <Chip
                            label={(lb.loadBalancerType || lb.loadBalancerTier || "Standard").toUpperCase()}
                            size="small"
                            sx={{ bgcolor: alpha("#0284c7", 0.1), color: "primary.main", fontWeight: 700, fontSize: 10, height: 20 }}
                          />
                        </TableCell>
                      )}
                      {visibleCols.dns && (
                        <TableCell>
                          <Tooltip title={lb.loadBalancerDNSname || lb.loadBalancerAddress} arrow>
                            <Typography variant="body2" sx={{ maxWidth: 270, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
                              {lb.loadBalancerDNSname || lb.loadBalancerAddress}
                            </Typography>
                          </Tooltip>
                          <Box
                            onClick={() => copyDNS(lb.loadBalancerDNSname || lb.loadBalancerAddress)}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, mt: 0.5, cursor: "pointer", color: "text.secondary", fontSize: 11, "&:hover": { color: "primary.main" } }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 11 }} />
                            Copy endpoint
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.cost && (
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontSize: 13 }}>${totalCostRow.toFixed(4)}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4 }}>
                            LB: ${(lb.loadBalancerCost || 0).toFixed(4)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            Data: ${(lb.loadBalancerDataProcessedCost || 0).toFixed(6)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 600 }}>
                            {(lb.loadBalancerRunningHours || lb.loadBalancerRunningHours).toFixed(1)}h runtime
                          </Typography>
                        </TableCell>
                      )}
                      {visibleCols.usage && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "left", justifyContent: "space-between", gap: 1, mb: 0.5 }}>

                            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>{usageVal.toFixed(4)} MB</Typography>
                            {/* <Chip label={usageLabel} size="small" color={usageColor} sx={{ height: 18, fontSize: 10 }} /> */}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #f1f5f9", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              Showing <strong>{sorted.length}</strong> of <strong>{RAW_DATA.length}</strong> load balancers
            </Typography>
          </Box>
        </Card>

        <Snackbar open={snack.open} autoHideDuration={2200} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity="success" variant="filled" sx={{ fontSize: 13, borderRadius: 2 }} onClose={() => setSnack(s => ({ ...s, open: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}